import { floorToLoadable, formatMass, loadFor, type Gym } from '@repwise/domain';
import { useState, type ReactNode } from 'react';
import { Button } from './ui.tsx';

/** Plate colours are the same in both themes, so their labels are too: white on red / blue, ink on the rest (≥ 4.9:1). */
const PLATE_CLASS: Record<number, string> = { 2500: 'bg-plate-25 text-white', 2000: 'bg-plate-20 text-white', 1500: 'bg-plate-15 text-on-now', 1000: 'bg-plate-10 text-on-now', 500: 'bg-plate-5 text-on-now' };

/**
 * J23 (C4s): the plate sheet for any weight, from the same solver as the session (loadFor), offline. Shows the bar,
 * the plates on each side, the collars, and "nearest you can load" when the exact weight can't be made.
 */
export function PlateSheet({ gym, weight, onClose }: { gym: Gym; weight: number; onClose: () => void }) {
  const u = gym.unit;
  const exact = loadFor(gym, weight);
  const nearest = exact ? weight : floorToLoadable(gym, weight);
  const side = exact ?? (nearest !== null ? loadFor(gym, nearest) : null);
  const below = weight < gym.bar + 2 * gym.collar;
  return (
    <div className="fixed inset-0 z-30 bg-black/50 flex items-end" role="dialog" aria-modal="true" aria-label={`Plates for ${formatMass(weight)} ${u}`} onClick={onClose}>
      <div className="w-full max-w-[480px] mx-auto bg-surface-raised rounded-t-[20px] p-5 space-y-3" onClick={(e) => e.stopPropagation()} style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }}>
        {/* C4s final (Wave 2.4 batch 3): the weight, each side in words and as plates, then the bar and collars */}
        <h2 className="text-headline">Plates</h2>
        <p className="text-center">
          <span className="num text-num-hero">{formatMass(weight)}</span> <span className="text-text-muted">{u}</span>
        </p>
        {below ? (
          <p>That's less than the bar ({formatMass(gym.bar)} {u}{gym.collar ? ' with collars' : ''}). Use the empty bar.</p>
        ) : side === null ? (
          <p>Your plates can't make this weight.</p>
        ) : (
          <>
            {!exact && nearest !== null && (
              <p className="text-miss" role="status">
                Nearest you can load: {formatMass(nearest)} {u}
              </p>
            )}
            <div className="bg-surface-sunken rounded-[14px] p-3 space-y-2">
              <PlateStrip gym={gym} weight={nearest!} />
              {side.length > 0 && (
                <ul className="flex flex-wrap gap-2" aria-label="Plates on each side">
                  {[...new Set(side)].map((p) => (
                    <li key={p} className="num text-sm rounded-full bg-surface-raised px-2 py-0.5">
                      {formatMass(p)} {u} × {side.filter((x) => x === p).length}
                    </li>
                  ))}
                </ul>
              )}
              <p>
                Each side: <span className="num font-semibold">{side.length ? side.map(formatMass).join(' + ') : 'nothing, just the bar'}</span> {side.length ? u : ''}
                {side.length > 1 && <span className="text-text-muted"> ({formatMass(side.reduce((a, b) => a + b, 0))} {u} per side)</span>}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <p className="bg-surface-sunken rounded-[12px] px-3 py-2">
                <span className="block text-xs text-text-muted">Bar</span>
                {formatMass(gym.bar)} {u}
              </p>
              <p className="bg-surface-sunken rounded-[12px] px-3 py-2">
                <span className="block text-xs text-text-muted">Collars</span>
                {gym.collar ? `${formatMass(gym.collar)} ${u} each` : 'None'}
              </p>
            </div>
          </>
        )}
        <Button kind="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

/** A weight you can tap to see its plates (J23). */
export function WeightButton({ gym, weight, children, className = '' }: { gym: Gym; weight: number; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={`underline decoration-dotted underline-offset-4 ${className}`} aria-label={`Plates for ${formatMass(weight)} ${gym.unit}`} onClick={() => setOpen(true)}>
        {children}
      </button>
      {open && <PlateSheet gym={gym} weight={weight} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Compact C4: the plates on one side, heaviest first (S01b). */
export function PlateStrip({ gym, weight }: { gym: Gym; weight: number }) {
  const side = loadFor(gym, weight) ?? [];
  return (
    <div className="bg-surface-sunken rounded-[10px] p-2 flex items-center gap-1.5" aria-hidden>
      <span className="w-2 h-7 bg-outline rounded-sm shrink-0" />
      {side.map((p, i) => (
        <span key={i} className={`${p >= 1000 ? 'h-9 min-w-9 text-sm' : 'h-6 min-w-7 text-xs'} px-1.5 rounded font-bold num grid place-items-center ${PLATE_CLASS[p] ?? 'bg-plate-change text-on-now'}`}>
          {formatMass(p)}
        </span>
      ))}
      <span className="h-1 flex-1 bg-outline rounded-full" />
    </div>
  );
}
